import { Paper, Tabs } from "@mantine/core";
import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import api from "../../api";
import { mieterstromInvoicingApiWithoutCamelization } from "../../api/subApis/mieterstromInvoicingApi";
import { usePersons } from "../../hooks/usePersons";
import { useSites } from "../../hooks/useSites";
import urls, { OptiSupportEndpoints } from "../../urls";
import type { Delivery } from "../../utils/backend-types";
import { PageContent } from "../BuildingBlocks/Layout/PageContent/PageContent";
import { AdvancePaymentsPlans } from "../DeliveryInvoices/AdvancePayments/AdvancePayments";
import { DeliveryInvoices } from "../DeliveryInvoices/DeliveryInvoices";
import { useContracts } from "../DeliveryInvoices/hooks/useContracts";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { OptiSupportHelpLink } from "../OptiSupportHelpLink/OptiSupportHelpLink";
import { ContractDeliveries } from "./ContractDeliveries/ContractDeliveries";
import "./DeliveryOverview.scss";

export const TABS = {
  TAB_CONTRACTS: "vertraege",
  TAB_INVOICES: "rechnungen",
  TAB_ADVANCE_PAYMENTS: "abschlaege"
} as const;

export const DEFAULT_SUBPAGE = TABS.TAB_INVOICES;

interface DeliveryOverviewProps {
  projectId: string | undefined;
  variantId: number | undefined;
}

function DeliveryOverview({ projectId, variantId }: DeliveryOverviewProps) {
  const { tab: activeTab } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    isLoading: isContractsQueryLoading,
    error: contractsQueryError,
    contracts
  } = useContracts({ projectId });
  const {
    isLoading: isDeliveriesQueryLoading,
    error: deliveriesQueryError,
    data: deliveries
  } = useQuery({
    queryKey: ["deliveries", { projectId }],
    queryFn: projectId ? () => fetchDeliveries(projectId) : skipToken
  });
  const {
    isLoading: isPersonsQueryLoading,
    error: personsQueryError,
    persons
  } = usePersons({ variantId });
  const {
    data: sites,
    isLoading: isSitesQueryLoading,
    error: sitesQueryError
  } = useSites({ projectId });

  function onContractsUpdated() {
    if (!projectId) {
      return;
    }

    queryClient.invalidateQueries({
      queryKey:
        mieterstromInvoicingApiWithoutCamelization.contracts.key(projectId)
    });
  }

  function onDeliveriesUpdated() {
    queryClient.invalidateQueries({
      queryKey: ["deliveries"]
    });
  }

  return (
    <PageContent className="DeliveryOverview">
      <Paper>
        <Tabs value={activeTab} onChange={(newTab) => navigate(`../${newTab}`)}>
          <Tabs.List>
            <Tabs.Tab component="a" value={TABS.TAB_CONTRACTS}>
              Verträge
            </Tabs.Tab>
            <Tabs.Tab component="a" value={TABS.TAB_INVOICES}>
              Rechnungen
            </Tabs.Tab>
            <Tabs.Tab component="a" value={TABS.TAB_ADVANCE_PAYMENTS}>
              Abschlagspläne
            </Tabs.Tab>
            {TABS.TAB_INVOICES === activeTab && (
              <OptiSupportHelpLink
                optiSupportEndpoint={OptiSupportEndpoints.MieterstromRechnungen}
                text="Hilfe zu Rechnungen"
              />
            )}
            {TABS.TAB_CONTRACTS === activeTab && (
              <OptiSupportHelpLink
                optiSupportEndpoint={
                  OptiSupportEndpoints.MieterstromVertraegeUndLieferungen
                }
                text="Hilfe zu Verträgen & Lieferungen"
              />
            )}
          </Tabs.List>

          <Tabs.Panel value={TABS.TAB_CONTRACTS}>
            <LoadOrError
              error={
                deliveriesQueryError ||
                contractsQueryError ||
                personsQueryError ||
                sitesQueryError
              }
              loading={
                isContractsQueryLoading ||
                isPersonsQueryLoading ||
                isDeliveriesQueryLoading ||
                isSitesQueryLoading
              }
            >
              {activeTab === TABS.TAB_CONTRACTS &&
                projectId &&
                variantId &&
                contracts &&
                deliveries &&
                persons && (
                  <ContractDeliveries
                    contracts={contracts}
                    deliveries={deliveries}
                    persons={persons}
                    projectId={projectId}
                    sites={sites || []}
                    variantId={variantId}
                    onContractsUpdated={onContractsUpdated}
                    onDeliveriesUpdated={onDeliveriesUpdated}
                  />
                )}
            </LoadOrError>
          </Tabs.Panel>
          <Tabs.Panel value={TABS.TAB_INVOICES}>
            <LoadOrError
              error={personsQueryError}
              loading={isPersonsQueryLoading}
            >
              {activeTab === TABS.TAB_INVOICES &&
                projectId &&
                variantId &&
                contracts && (
                  <DeliveryInvoices
                    contracts={contracts}
                    projectId={projectId}
                    variantId={variantId}
                  />
                )}
            </LoadOrError>
          </Tabs.Panel>
          <Tabs.Panel value={TABS.TAB_ADVANCE_PAYMENTS}>
            <LoadOrError
              error={personsQueryError}
              loading={isPersonsQueryLoading}
            >
              {activeTab === TABS.TAB_ADVANCE_PAYMENTS &&
                projectId &&
                contracts &&
                persons && (
                  <AdvancePaymentsPlans
                    contracts={contracts}
                    persons={persons}
                    projectId={projectId}
                  />
                )}
            </LoadOrError>
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </PageContent>
  );
}

async function fetchDeliveries(projectId: string) {
  const response = await api.get<Array<Delivery>>(
    urls.api.deliveries(projectId)
  );
  return response.data;
}

export { DeliveryOverview };
